import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, OverlayTrigger } from 'react-bootstrap';
import { User } from 'lucide-react';
import TaskAssigneePopover from './Popovers/TaskAssigneePopover';

const TaskCreateDialog = ({ show, onHide, onSubmit, initialStatusId, statusName, initialDate, members = [] }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [priority, setPriority] = useState(1);
    const [assignedTo, setAssignedTo] = useState(null);

    useEffect(() => {
        if (show) {
            setTitle('');
            setDescription('');
            setDueDate(initialDate || '');
            setPriority(1);
            setAssignedTo(null);
        }
    }, [show, initialStatusId, initialDate]);

    const handleSubmit = () => {
        if (!title.trim()) return;

        onSubmit({
            content: title,
            description,
            dueDate: dueDate ? new Date(dueDate).toISOString() : null,
            priority,
            statusId: initialStatusId,
            assignedTo // Add assignedTo
        });
        onHide();
    };

    const handleAssign = (userId) => {
        setAssignedTo(userId);
        document.body.click(); // Close popover
    };

    const renderAssigneeTrigger = () => {
        const assignedMember = members.find(m => m.id === assignedTo); // Match by Profile ID

        return (
            <OverlayTrigger
                trigger="click"
                placement="bottom"
                overlay={<TaskAssigneePopover members={members} assignedTo={assignedTo} onAssign={handleAssign} />}
                rootClose
                popperConfig={{ strategy: 'fixed' }}
            >
                <div
                    className="d-flex align-items-center gap-2 border rounded p-2 cursor-pointer bg-white"
                    title={assignedMember ? `Assigned to ${assignedMember.displayName}` : 'Assign Member'}
                >
                    <div
                        className="d-flex align-items-center justify-content-center rounded-circle border overflow-hidden"
                        style={{ width: 24, height: 24 }}
                    >
                        {assignedMember ? (
                            assignedMember.avatarUrl ? (
                                <img src={assignedMember.avatarUrl} alt={assignedMember.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                                <div className="w-100 h-100 d-flex align-items-center justify-content-center text-white" style={{ backgroundColor: assignedMember.color || '#6c757d', fontSize: '10px', fontWeight: 'bold' }}>
                                    {assignedMember.displayName?.substring(0, 2).toUpperCase()}
                                </div>
                            )
                        ) : (
                            <User size={14} className="text-muted opacity-50" />
                        )}
                    </div>
                    <span className="text-muted small">
                        {assignedMember ? assignedMember.displayName : 'Unassigned'}
                    </span>
                </div>
            </OverlayTrigger>
        );
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Add Task to {statusName || 'Board'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="What needs to be done?"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            autoFocus
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Add details..."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </Form.Group>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Due Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={dueDate}
                                    onChange={(e) => setDueDate(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Priority</Form.Label>
                                <Form.Select
                                    value={priority}
                                    onChange={(e) => setPriority(Number(e.target.value))}
                                >
                                    <option value={1}>Low</option>
                                    <option value={2}>Medium</option>
                                    <option value={3}>High</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>Assignee</Form.Label>
                        <div>
                            {renderAssigneeTrigger()}
                        </div>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={!title.trim()}>
                    Create Task
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default TaskCreateDialog;
